/*******************************************************************************
 * Copyright (c) 2024-2026 Malond Limited. All rights reserved.
 * The source code is the property of Malond Limited.
 ******************************************************************************/

import { isElectron, isTauri } from "./environment"

export interface DesktopAuthResponse {
  success: boolean
  sessionToken?: string
  session_token?: string
  session?: unknown
  error?: unknown
}

export const DesktopAuth = {
  /**
   * Выполняет Native API вход без браузерного Origin
   */
  async login(identifier: string, pass: string): Promise<DesktopAuthResponse> {
    if (isElectron()) {
      const win = window as unknown as {
        electronAuth: {
          login: (id: string, p: string) => Promise<DesktopAuthResponse>
        }
      }
      const res = await win.electronAuth.login(identifier, pass)
      const token = res.sessionToken || res.session_token || ""
      return {
        ...res,
        sessionToken: token,
        session_token: token,
      }
    }
    if (isTauri()) {
      const { invoke } = await import("@tauri-apps/api/core")
      const res = await invoke<DesktopAuthResponse>("kratos_login", {
        identifier,
        pass,
      })
      const token = res.sessionToken || res.session_token || ""
      return {
        ...res,
        sessionToken: token,
        session_token: token,
      }
    }
    throw new Error(
      "DesktopAuth is only available in Electron or Tauri environments"
    )
  },

  /**
   * Проверяет валидность сессии кассира
   */
  async whoami(): Promise<unknown | null> {
    if (isElectron()) {
      const win = window as unknown as {
        electronAuth: { whoami: () => Promise<unknown | null> }
      }
      return win.electronAuth.whoami()
    }
    if (isTauri()) {
      const { invoke } = await import("@tauri-apps/api/core")
      return invoke<unknown | null>("kratos_whoami")
    }
    return null
  },

  /**
   * Выполняет выход из системы
   */
  async logout(): Promise<boolean> {
    if (isElectron()) {
      const win = window as unknown as {
        electronAuth: { logout: () => Promise<boolean> }
      }
      return win.electronAuth.logout()
    }
    if (isTauri()) {
      const { invoke } = await import("@tauri-apps/api/core")
      return invoke<boolean>("kratos_logout")
    }
    return true
  },

  /**
   * Получение токена из аппаратного защищенного хранилища (safeStorage / Keyring)
   */
  async getToken(): Promise<string | null> {
    if (isElectron()) {
      const win = window as unknown as {
        electronAuth: { getToken: () => Promise<string | null> }
      }
      return win.electronAuth.getToken()
    }
    if (isTauri()) {
      const { invoke } = await import("@tauri-apps/api/core")
      return invoke<string | null>("get_secure_token")
    }
    return null
  },

  /**
   * Сохранение токена
   */
  async saveToken(token: string): Promise<boolean> {
    if (isElectron()) {
      const win = window as unknown as {
        electronAuth: { saveToken: (t: string) => Promise<boolean> }
      }
      return win.electronAuth.saveToken(token)
    }
    if (isTauri()) {
      const { invoke } = await import("@tauri-apps/api/core")
      return invoke<boolean>("save_secure_token", { token })
    }
    return false
  },

  /**
   * Удаление токена при выходе
   */
  async clearToken(): Promise<boolean> {
    if (isElectron()) {
      const win = window as unknown as {
        electronAuth: { clearToken: () => Promise<boolean> }
      }
      return win.electronAuth.clearToken()
    }
    if (isTauri()) {
      const { invoke } = await import("@tauri-apps/api/core")
      return invoke<boolean>("clear_secure_token")
    }
    return true
  },
}
